// src/pages/Dashboard.js
import React, { useState, useEffect } from 'react';
import { Card, CardContent, Typography, Grid, CircularProgress, Alert, Box, Container, Paper } from '@mui/material';
import { PostAdd, Send, Cloud, Timeline } from '@mui/icons-material';
import { motion } from 'framer-motion';
import PageHead from '../components/PageHead';

function Dashboard() {
  const [metrics, setMetrics] = useState({
    scheduledPosts: 0,
    publishedPosts: 0,
    platformsConnected: 0
  });
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const token = localStorage.getItem('token');
        const headers = {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        };

        const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

        // Fetch posts and WordPress connection status in parallel
        const [scheduledResponse, publishedResponse, wordpressResponse] = await Promise.all([
          fetch(`${API_URL}/posts/scheduled-posts`, { headers }),
          fetch(`${API_URL}/posts/published-posts`, { headers }),
          fetch(`${API_URL}/settings/wordpress-status`, { headers })
        ]);

        if (!scheduledResponse.ok || !publishedResponse.ok || !wordpressResponse.ok) {
          throw new Error('Failed to fetch dashboard data');
        }

        const scheduledData = await scheduledResponse.json();
        const publishedData = await publishedResponse.json();
        const wordpressData = await wordpressResponse.json();

        // Update metrics including platforms connected
        setMetrics({
          scheduledPosts: scheduledData.length,
          publishedPosts: publishedData.length,
          platformsConnected: wordpressData.connected ? 1 : 0
        });

        // Create recent activity including WordPress connection
        const allPosts = [
          ...scheduledData.map(post => ({
            ...post,
            type: 'scheduled',
            date: new Date(post.scheduled_date)
          })),
          ...publishedData.map(post => ({
            ...post,
            type: 'published',
            date: new Date(post.published_date)
          }))
        ];

        // Add WordPress connection/disconnection to activity if status changed
        if (wordpressData.connected) {
          allPosts.push({
            id: 'wp-connection',
            type: 'platform',
            date: new Date(),
            title: `WordPress site (${wordpressData.site_url}) connected`
          });
        }

        const sortedActivity = allPosts
          .sort((a, b) => b.date - a.date)
          .slice(0, 5)
          .map(post => ({
            id: post.id,
            message: post.type === 'platform' ? 
              post.title : 
              `Post "${post.title}" ${post.type === 'scheduled' ? 
                `scheduled for ${new Date(post.scheduled_date).toLocaleDateString()}` : 
                `published on ${new Date(post.published_date).toLocaleDateString()}`}`
          }));

        setRecentActivity(sortedActivity);
        setError(null);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        setError('Failed to load dashboard data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="80vh"
      >
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          <CircularProgress size={60} thickness={4} />
        </motion.div>
      </Box>
    );
  }

  const metricIcons = {
    scheduledPosts: <PostAdd />,
    publishedPosts: <Send />,
    platformsConnected: <Cloud />
  };

  return (
    <Container maxWidth="lg">
      <PageHead 
        title="Dashboard" 
        description="Your AutoBlogPilot content management overview"
      />
      <Box
        sx={{
          background: 'linear-gradient(145deg, #f6f8ff 0%, #f0f4ff 100%)',
          borderRadius: 4,
          p: 4,
          minHeight: '90vh',
        }}
      >
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Box mb={4}>
            <Typography 
              variant="h3" 
              component="h1" 
              gutterBottom 
              sx={{ 
                fontWeight: 'bold',
                background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
                backgroundClip: 'text',
                textFillColor: 'transparent',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Welcome to AutoBlogPilot
            </Typography>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              Your content management overview
            </Typography>
          </Box>

          {error && (
            <Alert 
              severity="error" 
              sx={{ 
                mb: 3,
                borderRadius: 2,
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
              }}
            >
              {error}
            </Alert>
          )}

          <Grid container spacing={3}>
            {Object.entries(metrics).map(([key, value]) => (
              <Grid item xs={12} sm={4} key={key}>
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  <Paper
                    elevation={2}
                    sx={{
                      borderRadius: 3,
                      overflow: 'hidden',
                      background: 'white',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                      },
                    }}
                  >
                    <Box p={3}>
                      <Box display="flex" alignItems="center" mb={2}>
                        <Box
                          sx={{
                            background: 'rgba(33, 150, 243, 0.1)',
                            borderRadius: '50%',
                            p: 1,
                            mr: 2,
                            color: 'primary.main',
                          }}
                        >
                          {metricIcons[key]}
                        </Box>
                        <Typography
                          variant="h6"
                          component="div"
                          sx={{ color: 'text.secondary' }}
                        >
                          {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                        </Typography>
                      </Box>
                      <Typography
                        variant="h3"
                        sx={{
                          fontWeight: 'bold',
                          color: 'primary.main',
                        }}
                      >
                        {value}
                      </Typography>
                    </Box>
                  </Paper>
                </motion.div>
              </Grid>
            ))}
          </Grid>

          <Box mt={6}>
            <Box display="flex" alignItems="center" mb={3}>
              <Timeline sx={{ mr: 2, color: 'primary.main' }} />
              <Typography variant="h5" component="h3" fontWeight="bold">
                Recent Activity
              </Typography>
            </Box>
            <Paper
              elevation={2}
              sx={{
                borderRadius: 3,
                overflow: 'hidden',
                background: 'white',
              }}
            >
              <Box p={3}>
                {recentActivity.length > 0 ? (
                  <motion.ul
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.5 }}
                    style={{ 
                      listStyleType: 'none', 
                      padding: 0,
                      margin: 0 
                    }}
                  >
                    {recentActivity.map((activity, index) => (
                      <motion.li
                        key={activity.id}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.1 }}
                      >
                        <Box
                          sx={{
                            p: 2,
                            borderBottom: index !== recentActivity.length - 1 ? '1px solid rgba(0,0,0,0.08)' : 'none',
                            '&:hover': {
                              background: 'rgba(0,0,0,0.02)',
                            },
                          }}
                        >
                          <Typography
                            variant="body1"
                            sx={{
                              color: 'text.primary',
                              fontWeight: activity.message.includes('published') ? 'bold' : 'normal',
                            }}
                          >
                            {activity.message}
                          </Typography>
                        </Box>
                      </motion.li>
                    ))}
                  </motion.ul>
                ) : (
                  <Typography color="text.secondary" textAlign="center" py={4}>
                    No recent activity to display
                  </Typography>
                )}
              </Box>
            </Paper>
          </Box>
        </motion.div>
      </Box>
    </Container>
  );
}

export default Dashboard;

